/**
 * WeQuote CRM — Unlinked Visitors Queue (Task 6.3)
 * Shows widget chats with no paid_customer match
 * Agents can search and assign these chats to companies
 */
import React, { useState, useEffect, useCallback } from 'react'
import { apiGet, apiPost } from '../../utils/api'
import { PageHeader } from '../shared/PageHeader'

interface UnlinkedChat {
  id: number
  visitor_name?: string
  visitor_email?: string
  page_url?: string
  created_at: string
  message_count?: number
}

interface PaidCustomer {
  id: number
  company_name: string
}

// ─── Search Modal for assigning unlinked chats ─────────────────────────────
const AssignModal: React.FC<{
  chatId: number; onClose: () => void; onAssigned: () => void
}> = ({ chatId, onClose, onAssigned }) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PaidCustomer[]>([])
  const [searching, setSearching] = useState(false)
  const [assigning, setAssigning] = useState(false)

  const doSearch = async () => {
    if (!query.trim()) {
      setResults([])
      return
    }
    setSearching(true)
    try {
      // The list endpoint filters on `search` (NOT `q`) — passing `q` was
      // ignored, so it returned the default alphabetical page and never the
      // actual matches. Send `search` + a high limit so all relevant companies
      // show. Also normalise the shape ({ customers: [...] }) defensively —
      // reading it as a bare array previously white-screened the app.
      const data = await apiGet<any>('/paid-customers', { search: query, limit: 100 })
      const list: PaidCustomer[] = Array.isArray(data)
        ? data
        : (data?.customers || data?.data || data?.results || [])
      setResults(Array.isArray(list) ? list : [])
    } catch {
      setResults([])
    } finally {
      setSearching(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(doSearch, 300)
    return () => clearTimeout(timer)
  }, [query])

  const handleAssign = async (customerId: number) => {
    setAssigning(true)
    try {
      await apiPost(`/paid-customers/resolve-unlinked/${chatId}`, { customer_id: customerId })
      onAssigned()
      onClose()
    } catch (err: any) {
      alert(err.message)
    } finally {
      setAssigning(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-fade-in">
      <div className="bg-surface rounded-2xl shadow-xl border border-border w-full max-w-lg max-h-[80vh] flex flex-col animate-slide-down">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-txt-primary">Assign to Company</h3>
          <button onClick={onClose} className="p-1 rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors text-xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-3 border-b border-border">
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search companies..."
            autoFocus
            className="w-full px-3 py-2.5 text-sm rounded-lg bg-surface-secondary border border-border text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 transition-colors"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {searching ? (
            <p className="text-sm text-txt-tertiary text-center py-4">Searching...</p>
          ) : results.length === 0 && query ? (
            <p className="text-sm text-txt-tertiary text-center py-4">No companies found</p>
          ) : !query ? (
            <p className="text-sm text-txt-tertiary text-center py-4">Type to search for a company</p>
          ) : (
            results.map(company => (
              <button
                key={company.id}
                onClick={() => handleAssign(company.id)}
                disabled={assigning}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-border hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors disabled:opacity-50"
              >
                <p className="text-sm font-medium text-txt-primary text-left">{company.company_name}</p>
                <span className="text-xs text-brand-600 font-semibold">Assign</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────
export const UnlinkedVisitorsPage: React.FC = () => {
  const [chats, setChats] = useState<UnlinkedChat[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedChat, setSelectedChat] = useState<number | null>(null)

  const fetchChats = useCallback(async () => {
    setLoading(true)
    try {
      const data = await apiGet<any>('/paid-customers/unlinked-chats')
      const chatList = Array.isArray(data) ? data : (data?.unlinkedChats || data?.chats || [])
      setChats(chatList)
    } catch (err) {
      console.error('Failed to load unlinked chats:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchChats()
  }, [fetchChats])

  const handleAssigned = () => {
    setSelectedChat(null)
    fetchChats()
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-txt-secondary">Loading unlinked visitors...</div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <PageHeader
        title="Unlinked Visitors Queue"
        subtitle="Widget chats awaiting company assignment"
        sticky
        actions={
          <span className="text-sm font-medium text-txt-secondary bg-surface-secondary border border-border px-3 py-1 rounded-full">
            {chats.length} unlinked
          </span>
        }
      />

      {/* Content */}
      <div className="p-6 max-w-5xl mx-auto">
        {chats.length === 0 ? (
          <div className="bg-surface rounded-xl border border-dashed border-border p-10 text-center">
            <svg className="w-12 h-12 mx-auto text-txt-tertiary mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 21l-4.35-4.35m0 0A7.5 7.5 0 103 10.5a7.5 7.5 0 0013.65 6.15z" />
            </svg>
            <p className="text-txt-secondary font-medium">No unlinked visitors</p>
            <p className="text-sm text-txt-tertiary mt-1">New widget chats now auto-link to their company on arrival.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {chats.map(chat => (
              <div key={chat.id} className="bg-surface rounded-xl border border-border p-4 hover:border-brand-500/30 transition-colors">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex items-center justify-center w-9 h-9 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 text-sm font-semibold shrink-0">
                      {(chat.visitor_name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-txt-primary truncate">
                          {chat.visitor_name || 'Unknown Visitor'}
                        </h3>
                        {!!chat.message_count && (
                          <span className="text-[11px] font-medium text-txt-secondary bg-surface-secondary border border-border px-1.5 py-0.5 rounded">
                            {chat.message_count} msg
                          </span>
                        )}
                      </div>
                      {chat.visitor_email && (
                        <p className="text-xs text-txt-secondary truncate">{chat.visitor_email}</p>
                      )}
                      <p className="text-[11px] text-txt-tertiary truncate">
                        {chat.page_url ? `${chat.page_url.replace(/^https?:\/\//, '').slice(0, 40)} · ` : ''}{formatDate(chat.created_at)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedChat(chat.id)}
                    className="shrink-0 px-3.5 py-2 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors whitespace-nowrap"
                  >
                    Assign to Company
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedChat && (
        <AssignModal
          chatId={selectedChat}
          onClose={() => setSelectedChat(null)}
          onAssigned={handleAssigned}
        />
      )}
    </div>
  )
}
